import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams } from 'react-router-dom';

function dollarsFromCents(cents) {
  return cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400000);
  if (d < 1) return 'today';
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export default function RefCodes() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState({ ref_codes: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('purchases-desc');
  const [filter, setFilter] = useState('');
  const [openCode, setOpenCode] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/ref-codes`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [tenantSlug]);

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  const visible = data.ref_codes
    .filter((c) => !filter || c.code.toLowerCase().includes(filter.toLowerCase()) || (c.owner_name || '').toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      switch (sort) {
        case 'code-asc': return a.code.localeCompare(b.code);
        case 'owner-asc': return (a.owner_name || '').localeCompare(b.owner_name || '');
        case 'purchases-desc': return b.analytics.purchase_count - a.analytics.purchase_count;
        case 'revenue-desc': return b.analytics.revenue_cents - a.analytics.revenue_cents;
        default: return 0;
      }
    });

  const totals = data.ref_codes.reduce((acc, c) => ({
    purchases: acc.purchases + c.analytics.purchase_count,
    revenue: acc.revenue + c.analytics.revenue_cents,
    with_traffic: acc.with_traffic + (c.analytics.purchase_count > 0 ? 1 : 0),
  }), { purchases: 0, revenue: 0, with_traffic: 0 });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Ref codes</h1>
        <div className="text-sm text-muted-foreground">
          {data.count} registered · {totals.with_traffic} attributed · {totals.purchases} total purchases · {dollarsFromCents(totals.revenue)}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Filter by code or owner…"
          className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-3 py-1.5 rounded-md border border-input bg-background text-sm"
        >
          <option value="purchases-desc">Purchases (most first)</option>
          <option value="revenue-desc">Revenue (highest first)</option>
          <option value="code-asc">Code A-Z</option>
          <option value="owner-asc">Owner A-Z</option>
        </select>
      </div>

      {/* Mobile: stacked cards (the table below is md+ only) */}
      <div className="md:hidden space-y-2">
        {visible.map((c) => (
          <div
            key={c.id}
            onClick={() => setOpenCode(openCode === c.code ? null : c.code)}
            className={`border border-border rounded-md p-3 bg-card text-sm cursor-pointer ${openCode === c.code ? 'ring-1 ring-primary/50' : ''}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs font-medium">?ref={c.code}</span>
              {c.active ? (
                <span className="text-xs text-green-700 dark:text-green-300">active</span>
              ) : (
                <span className="text-xs text-muted-foreground">inactive</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{c.owner_name}</span>
              {c.owner_kind === 'auto_detected' ? (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300" title={c.notes || ''}>auto</span>
              ) : (c.owner_kind && <span>{c.owner_kind}</span>)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Purchases: {c.analytics.purchase_count || '—'} · Revenue: {c.analytics.purchase_count > 0 ? dollarsFromCents(c.analytics.revenue_cents) : '—'} · Last seen: {timeAgo(c.analytics.last_purchase_at)}
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="text-center text-muted-foreground py-6 text-sm">No ref codes match.</div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Owner</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium text-right">Purchases</th>
              <th className="px-3 py-2 font-medium text-right">Revenue</th>
              <th className="px-3 py-2 font-medium">Last seen</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr
                key={c.id}
                className={`border-t border-border hover:bg-accent/30 cursor-pointer ${openCode === c.code ? 'bg-accent/40' : ''}`}
                onClick={() => setOpenCode(openCode === c.code ? null : c.code)}
              >
                <td className="px-3 py-2 font-mono text-xs">?ref={c.code}</td>
                <td className="px-3 py-2">{c.owner_name}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {c.owner_kind === 'auto_detected' ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300" title={c.notes || ''}>auto</span>
                  ) : (c.owner_kind || '—')}
                </td>
                <td className="px-3 py-2 text-right">{c.analytics.purchase_count || '—'}</td>
                <td className="px-3 py-2 text-right">{c.analytics.purchase_count > 0 ? dollarsFromCents(c.analytics.revenue_cents) : '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{timeAgo(c.analytics.last_purchase_at)}</td>
                <td className="px-3 py-2">
                  {c.active ? (
                    <span className="text-xs text-green-700 dark:text-green-300">active</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">inactive</span>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No ref codes match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openCode && (
        <RefCodeDrill tenantSlug={tenantSlug} code={openCode} onClose={() => setOpenCode(null)} />
      )}

      {data.count === 0 && (
        <div className="mt-4 text-sm text-muted-foreground">
          No ref codes registered for this tenant. They get seeded from the Sheet's NIGHTLIFE tab during the import, only LIV Miami has a registry today.
        </div>
      )}
    </div>
  );
}

function RefCodeDrill({ tenantSlug, code, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setData(null); setError(null);
    apiFetch(`/api/tenants/${tenantSlug}/ref-codes/${encodeURIComponent(code)}/drill`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message));
  }, [tenantSlug, code]);

  return (
    <div className="mt-4 border border-border rounded-md p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium">
          Drill: <span className="font-mono">?ref={code}</span>
        </div>
        <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">close</button>
      </div>
      {error && <div className="text-sm text-destructive">Error: {error}</div>}
      {!data && !error && <div className="text-sm text-muted-foreground">Loading…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
            <Stat label="Sessions" value={data.conversion.session_count} />
            <Stat label="Purchases" value={data.conversion.purchase_count} />
            <Stat label="Conversion" value={data.conversion.conversion_pct != null ? `${data.conversion.conversion_pct}%` : '—'} />
            <Stat label="Revenue" value={dollarsFromCents(data.conversion.revenue_cents)} />
          </div>
          <div className="text-xs text-muted-foreground mb-2">Top events ({data.top_events.length})</div>
          {data.top_events.length === 0 ? (
            <div className="text-sm text-muted-foreground">No attributed purchases yet.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">Event</th>
                  <th className="py-1 text-right">Purchases</th>
                  <th className="py-1 text-right">Revenue</th>
                  <th className="py-1">Last</th>
                </tr>
              </thead>
              <tbody>
                {data.top_events.map((e) => (
                  <tr key={e.listing_id} className="border-t border-border">
                    <td className="py-1.5">{e.listing_title || e.listing_slug || e.listing_id}</td>
                    <td className="py-1.5 text-right">{e.purchase_count}</td>
                    <td className="py-1.5 text-right">{dollarsFromCents(e.revenue_cents)}</td>
                    <td className="py-1.5 text-muted-foreground text-xs">{timeAgo(e.last_purchase_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-border rounded-md p-2 bg-background">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
